// src/components/auth/LoginForm.jsx
import { useState, useEffect } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { auth } from "../../firebase/config";
import { useAuth } from "../../hooks/useAuth";
import { ROUTES } from "../../constants";
import { Button } from "../common/Button";
import { Input } from "../common/Input";
import { Alert } from "../common/Alert";
import { Loading } from "../common/Loading";
import apiService from "../../services/api.service";
import { FaEnvelope, FaLock, FaUser } from "react-icons/fa";
import "../../assets/css/auth.css";

export default function LoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { loading, isAuthenticated, hasAdminAccess } = useAuth();

  useEffect(() => {
    if (!loading && isAuthenticated) {
      if (hasAdminAccess) {
        navigate(ROUTES.DASHBOARD);
      } else {
        navigate(ROUTES.HOME);
      }
    }
  }, [loading, isAuthenticated, hasAdminAccess, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      await signInWithEmailAndPassword(auth, email, password);
      apiService.clearTokenCache();
      await auth.currentUser.getIdToken(true);

      try {
        await apiService.updateLastLogin();
      } catch (err) {
        console.warn("No se pudo actualizar último login:", err);
      }

    } catch (err) {
      console.error("Error en login:", err);
      let errorMessage = "Error al iniciar sesión";
      if (err.code === "auth/user-not-found") errorMessage = "No existe una cuenta con este email";
      else if (err.code === "auth/wrong-password") errorMessage = "Contraseña incorrecta";
      else if (err.code === "auth/invalid-email") errorMessage = "Email inválido";
      else if (err.code === "auth/too-many-requests") errorMessage = "Demasiados intentos fallidos. Intenta más tarde";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--bg-body)"
      }}>
        <Loading text="Verificando sesión..." />
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        {/* Header */}
        <div className="auth-header">
          <FaUser size={48} color="var(--primary)" style={{ marginBottom: "8px" }} />
          <h1 className="auth-title">
            Bienvenido
          </h1>
          <p className="auth-subtitle">
            Inicia sesión para continuar
          </p>
        </div>

        {error && (
          <Alert variant="error" onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleLogin} className="auth-form">
          <Input
            type="email"
            label="Email"
            placeholder="tu@email.com"
            icon={<FaEnvelope color="var(--primary)" />}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isLoading}
          />

          <Input
            type="password"
            label="Contraseña"
            placeholder="••••••••"
            icon={<FaLock color="var(--primary)" />}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={isLoading}
          />

          <div className="auth-actions">
            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              disabled={isLoading}
              className="btn-auth-primary"
            >
              {isLoading ? "Iniciando sesión..." : "Iniciar sesión"}
            </Button>

            <Button
              type="button"
              variant="secondary"
              size="lg"
              fullWidth
              onClick={() => navigate(ROUTES.REGISTER)}
              disabled={isLoading}
              className="btn-auth-secondary"
            >
              ¿No tienes cuenta? Regístrate
            </Button>
          </div>
        </form>

        <div className="auth-footer">
          <p className="auth-footer-text">
            Sistema Municipal de Gestión de Reclamos
          </p>
        </div>
      </div>
    </div>
  );
}
